import { ArrowRight, MapPin, Route, Navigation, Receipt, Users, CheckCircle2 } from "lucide-react";
import { Container, Reveal, SectionHeader } from "./common";

const pins = [
    { x: "18%", y: "30%", label: "A" },
    { x: "42%", y: "22%", label: "B" },
    { x: "66%", y: "40%", label: "C" },
    { x: "80%", y: "62%", label: "D" },
    { x: "55%", y: "72%", label: "E" },
];

const capabilities = [
    [MapPin, "GPS Check-in"],
    [Route, "Smart Routes"],
    [Navigation, "Planned Visits"],
    [Receipt, "Expenses"],
    [Users, "Customer Visits"],
    [CheckCircle2, "Team Performance"],
];

const team = [
    { name: "Rahul Sharma", pct: 90 },
    { name: "Priya Nair", pct: 88 },
    { name: "Arun Thomas", pct: 76 },
];

const FieldSales = () => (
    <section id="field-sales" data-testid="field-sales-section" className="bg-[#F7FEF9]/60 py-20 lg:py-28">
        <Container>
            <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
                {/* Map UI */}
                <Reveal>
                    <div data-testid="field-sales-map-ui" className="grid gap-4">
                        <div className="relative overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-xl shadow-slate-900/[0.06]">
                            {/* faux map */}
                            <div className="relative h-[300px] bg-[#F3F7F4]">
                                <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
                                    <defs>
                                        <pattern id="mapgrid" width="46" height="46" patternUnits="userSpaceOnUse">
                                            <path d="M46 0H0V46" fill="none" stroke="#E2E8F0" strokeWidth="1" />
                                        </pattern>
                                    </defs>
                                    <rect width="100%" height="100%" fill="url(#mapgrid)" />
                                    {/* route */}
                                    <path
                                        d="M 60 90 Q 140 40 190 66 T 300 120 Q 360 170 320 216"
                                        fill="none"
                                        stroke="#16A34A"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeDasharray="7 7"
                                        className="animate-dash-flow"
                                        opacity="0.7"
                                    />
                                </svg>
                                {pins.map((p) => (
                                    <span
                                        key={p.label}
                                        className="absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-[#16A34A] text-[10px] font-bold text-white shadow-lg"
                                        style={{ left: p.x, top: p.y }}
                                    >
                                        {p.label}
                                    </span>
                                ))}
                                <span className="absolute bottom-3 left-3 rounded-full bg-white/90 px-3 py-1.5 text-[10px] font-semibold text-[#475569] shadow-sm backdrop-blur">
                                    Field route · Bengaluru South · 24.6 km
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2">
                            <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
                                <p className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">Today's Plan</p>
                                <div className="mt-3 flex items-end justify-between">
                                    <div>
                                        <p className="font-display text-2xl font-extrabold text-[#111827]">6<span className="text-[#94A3B8]">/8</span></p>
                                        <p className="text-[11px] font-medium text-[#64748B]">visits completed</p>
                                    </div>
                                    <div className="relative h-14 w-14">
                                        <svg viewBox="0 0 56 56" className="h-full w-full -rotate-90">
                                            <circle cx="28" cy="28" r="24" fill="none" stroke="#F0FDF4" strokeWidth="7" />
                                            <circle cx="28" cy="28" r="24" fill="none" stroke="#16A34A" strokeWidth="7" strokeLinecap="round" strokeDasharray="150.8" strokeDashoffset="37.7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
                                <p className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">Team Performance</p>
                                <div className="mt-3 grid gap-2.5">
                                    {team.map((t) => (
                                        <div key={t.name} className="flex items-center gap-2">
                                            <span className="w-24 truncate text-[11px] font-semibold text-[#334155]">{t.name}</span>
                                            <div className="h-1.5 flex-1 rounded-full bg-[#F1F5F9]">
                                                <div className="h-1.5 rounded-full bg-[#16A34A]" style={{ width: `${t.pct}%` }} />
                                            </div>
                                            <span className="text-[10px] font-bold text-[#111827]">{t.pct}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </Reveal>

                <div>
                    <SectionHeader
                        align="left"
                        eyebrow="Field Sales"
                        title="Put your field sales team in control."
                        sub="Plan the day, optimize routes, check in from the field and log visits and expenses — while managers see everything live."
                    />
                    <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {capabilities.map(([Icon, label], i) => (
                            <Reveal key={label} delay={i * 0.05}>
                                <div className="flex flex-col items-start gap-2.5 rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#16A34A]/30">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F0FDF4]">
                                        <Icon className="h-4 w-4 text-[#16A34A]" />
                                    </span>
                                    <span className="text-[13px] font-semibold text-[#111827]">{label}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <a
                            href="#pricing"
                            data-testid="field-sales-explore-link"
                            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#111827] px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
                        >
                            Explore Field Sales
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </a>
                    </Reveal>
                </div>
            </div>
        </Container>
    </section>
);

export default FieldSales;
